// src/components/pages/SignUp.js

import React, { useState } from "react";
import {
  Button,
  Container,
  Backdrop,
  Box,
  CircularProgress,
  Typography,
  Grid,
  Snackbar,
  Alert,
} from "@mui/material";
import { createUserWithEmailAndPassword, getAuth } from "firebase/auth";
import { useTheme } from "styled-components";
import { useNavigate } from "react-router-dom"; // For navigation

const SignUpPage = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const theme = useTheme();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false); // For handling snackbar state
  const auth = getAuth();

  const handleSignUp = async () => {
    setLoading(true);
    setError(null);
    try {
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        email,
        password
      );
      const user = userCredential.user;
      await handlePostAuth(user); // Call the post-authentication function

      console.log("User signed up successfully:", user);
    } catch (error) {
      console.error("Error signing up:", error.message);
      const friendlyMessage = getFriendlyErrorMessage(error.code); // Convert the error code
      setError(friendlyMessage); // Set the friendly error message
      setSnackbarOpen(true); // Open the snackbar on error
      setLoading(false);
    }
  };

  // Function to map Firebase errors to friendly messages (updated)
  const getFriendlyErrorMessage = (errorCode) => {
    switch (errorCode) {
      case "auth/email-already-in-use":
        return "An account with this email already exists.";
      case "auth/invalid-email":
        return "Please enter a valid email address.";
      case "auth/operation-not-allowed":
        return "Email/password accounts are not enabled.";
      case "auth/weak-password":
        return "Your password is too weak. Please use a stronger password.";
      case "auth/network-request-failed":
        return "Network error. Please check your connection and try again.";
      default:
        return "An unexpected error occurred. Please try again later.";
    }
  };

  // Handle closing the snackbar
  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  // Function to handle post-authentication logic
  const handlePostAuth = async (user) => {
    await handleThirdPartyAPICalls(user);
    navigate("/challenge/profile/edit"); // Redirect to profile edit page after authentication and API calls
    setLoading(false); // Stop the loading spinner
  };

  // Helper function to post form data to the API
  const postFormData = async (url, data) => {
    const response = await fetch(url, {
      method: "POST",
      body: data,
    });
    if (!response.ok) {
      throw new Error(`API request failed with status ${response.status}`);
    }
    return await response.text();
  };

  // Function to handle third-party API calls
  const handleThirdPartyAPICalls = async (user) => {
    const token = await user.getIdToken();

    const socialLoginJWTURL = process.env.REACT_APP_SOCIAL_LOGIN_JWT_URL;
    const data = new FormData();
    data.append("fbtoken", token);
    data.append("currentUser", JSON.stringify(user));

    try {
      const socialLoginResponse = await postFormData(socialLoginJWTURL, data);
      console.log("Social login JWT response:", socialLoginResponse);

      const socialAuthenticateURL =
        process.env.REACT_APP_SOCIAL_AUTHENTICATE_URL;
      const authData = new FormData();
      authData.append("fbtoken", token);

      const authResponseData = await postFormData(
        socialAuthenticateURL,
        authData
      );
      console.log("Social authentication response:", authResponseData);

      // Store response in localStorage
      localStorage.setItem("authResponseData", authResponseData);
    } catch (apiError) {
      console.error("Error with third-party API calls:", apiError.message);
      setError("Failed to authenticate. Please try again.");
    }
  };

  return (
    <>
      {/* Full-screen overlay with circular loader */}
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <CircularProgress color="inherit" />
          <Typography variant="h6" sx={{ mt: 2 }}>
            Signing up...
          </Typography>
        </Box>
      </Backdrop>

      <Container maxWidth="xs">
        <Box sx={{ mt: 4 }}>
          <Typography
            variant="h4"
            gutterBottom
            sx={{ fontFamily: "basic-sans", fontWeight: "bold" }}
          >
            Sign Up
          </Typography>

          {/* Email and Password Section */}
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <input
                type="email"
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                style={{
                  width: "100%",
                  padding: "8px",
                  paddingLeft: "20px",
                  marginBottom: "2px",
                  fontSize: "16px",
                  fontFamily: "basic-sans",
                  borderRadius: "50px",
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <input
                type="password"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                style={{
                  width: "100%",
                  padding: "8px",
                  paddingLeft: "20px",
                  marginBottom: "2px",
                  fontSize: "16px",
                  fontFamily: "basic-sans",
                  borderRadius: "50px",
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <Button
                onClick={handleSignUp}
                variant="contained"
                fullWidth
                sx={{
                  backgroundColor: theme.colors.darkBlue,
                  border: `1px solid ${theme.colors.darkGray}`,
                  color: theme.colors.white,
                  borderRadius: "50px",
                  padding: "10px 30px",
                  marginBottom: "40px",
                  fontFamily: "basic-sans",
                  fontWeight: "bold",
                  textTransform: "none",
                  boxShadow: "none",
                  "&:hover": {
                    backgroundColor: theme.colors.green,
                    border: `1px solid ${theme.colors.green}`,
                    boxShadow: "none",
                  },
                }}
              >
                Sign Up
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Container>

      {/* Snackbar for error messages */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={handleSnackbarClose} severity="error" variant="filled">
          {error}
        </Alert>
      </Snackbar>
    </>
  );
};

export default SignUpPage;
